import { ReactNode } from "react";
import { Focusable, TooltipTrigger } from "react-aria-components";
import Link from "../../links/Link";
import { TooltipBubble } from "../TooltipBubble";

interface ElementIconLinkProps {
  /**
   * The destination route.
   */
  to: string;
  /**
   * The accessible label for the link (used for aria-label).
   */
  label: string;
  /**
   * The icon content (typically an SVG).
   *
   * **Accessibility:** The link provides the accessible name via `aria-label`.
   * To avoid duplicate announcements, pass the SVG with `aria-hidden` (and avoid `title`/`aria-label` on the SVG).
   */
  children: ReactNode;
  /**
   * Optional text for the tooltip bubble. Only provide this if it adds information beyond `label`.
   * If omitted, the tooltip will show `label` visually but is hidden from screen readers to
   * prevent redundant announcements. Ensure `label` contains all essential information.
   */
  tooltipText?: string;
}

/**
 * Renders an icon that links to another page, with an accessible tooltip.
 *
 * **When to use:**
 * - Navigation-based icons (e.g., Dream Team badge linking to Team of the Week)
 * - Status icons that double as navigation links
 *
 * **When NOT to use:**
 * - For interactive actions that don't change the URL (use `ElementIconButton` instead)
 * - For non-interactive status indicators (use `ElementIcon` instead)
 *
 * **Accessibility:**
 * - The link provides the accessible name via `aria-label={label}` - this is always announced
 * - Provide `tooltipText` only when it adds information beyond `label` (e.g., extra context or explanation)
 * - If `tooltipText` is omitted, the tooltip shows `label` visually but is hidden from screen readers
 * - Ensure `label` contains all essential information for screen reader users
 * - Pass SVG icons with `aria-hidden` to avoid duplicate announcements
 *
 * @example
 * ```tsx
 * // Good - navigation icon with tooltip
 * <ElementIconLink to="/team-of-the-week" label="Team of the Week">
 *   <DreamTeam aria-hidden />
 * </ElementIconLink>
 * ```
 */
const ElementIconLink = ({
  to,
  label,
  children,
  tooltipText,
}: ElementIconLinkProps) => (
  <TooltipTrigger delay={100}>
    <Focusable>
      <Link to={to} aria-label={label}>
        {children}
      </Link>
    </Focusable>
    <TooltipBubble>
      {tooltipText && tooltipText !== label ? (
        tooltipText
      ) : (
        <span aria-hidden="true">{label}</span>
      )}
    </TooltipBubble>
  </TooltipTrigger>
);

export default ElementIconLink;
